import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  InfoOutlined,
  DeleteOutline,
  SearchOutlined,
  FilterListOutlined,
  PersonAddOutlined,
  DownloadOutlined,
  PeopleOutlined,
  AdminPanelSettingsOutlined,
  SchoolOutlined,
  BusinessOutlined,
} from '@mui/icons-material';

function UserList() {
  const server_url = process.env.REACT_APP_API_URI;
  const [data, setUserData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    admins: 0,
    students: 0,
    faculty: 0,
    guests: 0,
  });

  const getAllUser = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${server_url}/user/get-all-user?page=${page}&limit=${limit}`,
      );
      const users = response?.data ?? [];
      const total = response?.data?.totalPages ?? 1;
      setUserData(users.data);
      setFilteredData(users.data);
      setTotalPages(total);

      // Calculate stats
      const userStats = {
        total: response?.data?.total || users.data?.length || 0,
        admins: users.data?.filter((u) => u.role === 1).length || 0,
        students: users.data?.filter((u) => u.role === 2).length || 0,
        faculty: users.data?.filter((u) => u.role === 3).length || 0,
        guests: users.data?.filter((u) => u.role === 4).length || 0,
      };
      setStats(userStats);
    } catch (err) {
      console.error('Error fetching users:', err);
      setUserData([]);
      setFilteredData([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllUser();
  }, [page, limit]);

  useEffect(() => {
    let filtered = [...data];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user?.school?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Apply role filter
    if (roleFilter) {
      filtered = filtered.filter((user) => user?.role === Number(roleFilter));
    }

    setFilteredData(filtered);
  }, [searchTerm, roleFilter, data]);

  const handleUserDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await axios.post(`${server_url}/user/delete-user`, {
          id,
        });
        alert(response?.data?.msg ?? 'User deleted.');
        if (response?.data?.status) getAllUser();
      } catch (err) {
        console.error('Error deleting user:', err);
      }
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['ID', 'Email', 'Username', 'Subject', 'School', 'Role'],
      ...filteredData.map((user) => [
        user._id,
        user.email,
        user.username || 'N/A',
        user.subject || 'N/A',
        user.school || 'N/A',
        roles[user.role] || 'Unknown',
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const roles = {
    1: 'Admin',
    2: 'Student',
    3: 'Faculty',
    4: 'Guest User',
  };

  const roleColors = {
    1: 'bg-purple-100 text-purple-800',
    2: 'bg-blue-100 text-blue-800',
    3: 'bg-green-100 text-green-800',
    4: 'bg-gray-100 text-gray-800',
  };

  const paginationOptions = [10, 20, 50, 100];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
          User Management
        </h1>
        <p className="text-gray-600">Manage and monitor all user accounts</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Total Users</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.total}
              </h3>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <PeopleOutlined className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Admins</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.admins}
              </h3>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <AdminPanelSettingsOutlined className="text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Students</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.students}
              </h3>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <SchoolOutlined className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Faculty</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.faculty}
              </h3>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <BusinessOutlined className="text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by email, username, or school..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Role Filter */}
          <div>
            <div className="relative">
              <FilterListOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
              >
                <option value="">All Roles</option>
                <option value="1">Admin</option>
                <option value="2">Student</option>
                <option value="3">Faculty</option>
                <option value="4">Guest User</option>
              </select>
            </div>
          </div>

          {/* Export Button */}
          <div className="flex gap-2">
            <button
              onClick={handleExport}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors w-full"
            >
              <DownloadOutlined fontSize="small" />
              <span className="hidden md:inline">Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <label className="font-medium text-gray-700">Show:</label>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {paginationOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <span className="text-gray-600">entries</span>
        </div>

        <div className="text-gray-600">
          Showing {filteredData.length > 0 ? (page - 1) * limit + 1 : 0} to{' '}
          {Math.min(page * limit, filteredData.length)} of {filteredData.length}{' '}
          {filteredData.length !== data.length &&
            `(filtered from ${data.length})`}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <tr>
                  <th className="py-4 px-6 text-left font-semibold">User</th>
                  <th className="py-4 px-6 text-left font-semibold">Email</th>
                  <th className="py-4 px-6 text-left font-semibold">Subject</th>
                  <th className="py-4 px-6 text-left font-semibold">School</th>
                  <th className="py-4 px-6 text-left font-semibold">Role</th>
                  <th className="py-4 px-6 text-center font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {Array.isArray(filteredData) && filteredData.length > 0 ? (
                  filteredData.map((user) => (
                    <tr
                      key={user?._id ?? Math.random()}
                      className="hover:bg-blue-50 transition-colors"
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                            {user?.username?.charAt(0).toUpperCase() ||
                              user?.email?.charAt(0).toUpperCase() ||
                              'U'}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800">
                              {user?.username || 'Unknown'}
                            </p>
                            <p className="text-sm text-gray-500">
                              ID: {user?._id?.slice(-8) ?? 'N/A'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-700">
                        {user?.email ?? 'N/A'}
                      </td>
                      <td className="py-4 px-6 text-gray-700">
                        {user?.subject ?? 'N/A'}
                      </td>
                      <td className="py-4 px-6 text-gray-700">
                        {user?.school ?? 'N/A'}
                      </td>
                      <td className="py-4 px-6">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${roleColors[user?.role] ?? 'bg-gray-100 text-gray-800'}`}
                        >
                          {roles[user?.role] ?? 'Unknown'}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center gap-2">
                          <Link
                            to={`/admin/user/${user?._id}`}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <InfoOutlined fontSize="small" />
                          </Link>
                          <button
                            onClick={() => handleUserDelete(user?._id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            disabled={!user?._id}
                            title="Delete User"
                          >
                            <DeleteOutline fontSize="small" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <PeopleOutlined className="text-gray-400 text-5xl" />
                        <p className="text-gray-500 text-lg font-medium">
                          No users found
                        </p>
                        <p className="text-gray-400 text-sm">
                          Try adjusting your search or filters
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-md p-4 mt-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <button
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page === 1}
            className="w-full md:w-auto px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 transition-colors"
          >
            Previous
          </button>

          <div className="flex items-center gap-2">
            {[...Array(Math.min(totalPages, 5))].map((_, index) => {
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = index + 1;
              } else if (page <= 3) {
                pageNumber = index + 1;
              } else if (page >= totalPages - 2) {
                pageNumber = totalPages - 4 + index;
              } else {
                pageNumber = page - 2 + index;
              }

              return (
                <button
                  key={index}
                  onClick={() => setPage(pageNumber)}
                  className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                    page === pageNumber
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={page === totalPages}
            className="w-full md:w-auto px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default UserList;
